import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SeriesForm from './SeriesForm';
import Modal from './Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { downloadCardImage } from '../utils/downloadCard';
import { tagAtPatchOnEdit, byTagAtOrCreatedAt } from '../utils/tagTimestamps';
import { availablePeople } from '../utils/people';
import { useLongPress } from '../hooks/useLongPress';
import { useAuth } from '../AuthContext';

const WATCHED_AT_FIELD = {
  Joy: 'watched_with_joy_at',
  Rick: 'watched_with_rick_at',
  Lisa: 'watched_with_lisa_at',
  Gionnan: 'watched_with_gionnan_at',
};

const WATCHED_WITH_FIELD = {
  Joy: 'watched_with_joy',
  Rick: 'watched_with_rick',
  Lisa: 'watched_with_lisa',
  Gionnan: 'watched_with_gionnan',
  // No 'watched_with_youssra' column on series, so Youssra never shows up as an option here.
};

function App() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [originalSeries, setOriginalSeries] = useState([]);
  const [series, setSeries] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [universeFilter, setUniverseFilter] = useState('');
  const [watchedWithFilter, setWatchedWithFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('original');

  const [showAddForm, setShowAddForm] = useState(false);

  const cardRefs = useRef([]);
  const downloadLongPress = useLongPress();
  const editLongPress = useLongPress();

  const [editingSerie, setEditingSerie] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPoster, setEditPoster] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const openEdit = (serie) => {
    setEditingSerie(serie);
    setEditForm({
      title: serie.title || '',
      season: serie.season || '',
      rating: serie.rating || '',
      thoughts: serie.thoughts || '',
      marvel: serie.marvel || false,
      dc: serie.dc || false,
      watchedWithJoy: serie.watched_with_joy || false,
      watchedWithRick: serie.watched_with_rick || false,
      watchedWithLisa: serie.watched_with_lisa || false,
      watchedWithGionnan: serie.watched_with_gionnan || false,
    });
    setEditPoster(null);
    setEditStatus('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.title || !editForm.rating) { setEditStatus('Please fill in a title and rating.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      let newPosterUrl = null;
      if (editPoster) newPosterUrl = await uploadPoster(editPoster);

      const tagAtPatch = tagAtPatchOnEdit([
        { wasOn: !!editingSerie.watched_with_joy, isOn: editForm.watchedWithJoy, atField: 'watched_with_joy_at' },
        { wasOn: !!editingSerie.watched_with_rick, isOn: editForm.watchedWithRick, atField: 'watched_with_rick_at' },
        { wasOn: !!editingSerie.watched_with_lisa, isOn: editForm.watchedWithLisa, atField: 'watched_with_lisa_at' },
        { wasOn: !!editingSerie.watched_with_gionnan, isOn: editForm.watchedWithGionnan, atField: 'watched_with_gionnan_at' },
      ]);

      const patch = {
        title: editForm.title,
        rating: parseFloat(editForm.rating),
        marvel: editForm.marvel,
        dc: editForm.dc,
        watched_with_joy: editForm.watchedWithJoy,
        watched_with_rick: editForm.watchedWithRick,
        watched_with_lisa: editForm.watchedWithLisa,
        watched_with_gionnan: editForm.watchedWithGionnan,
        thoughts: editForm.thoughts || '',
        ...tagAtPatch,
      };
      if (editForm.season) patch.season = editForm.season;
      if (newPosterUrl) patch.poster_url = newPosterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/series?id=eq.${editingSerie.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('Update failed');

      const updated = {
        ...editingSerie,
        title: editForm.title,
        season: editForm.season,
        rating: parseFloat(editForm.rating),
        thoughts: editForm.thoughts || '',
        marvel: editForm.marvel,
        dc: editForm.dc,
        watched_with_joy: editForm.watchedWithJoy,
        watched_with_rick: editForm.watchedWithRick,
        watched_with_lisa: editForm.watchedWithLisa,
        watched_with_gionnan: editForm.watchedWithGionnan,
        ...tagAtPatch,
        ...(newPosterUrl ? { poster_url: newPosterUrl } : {}),
      };
      const updateList = (list) => list.map((s) => s.id === editingSerie.id ? updated : s);
      setOriginalSeries(updateList);
      setSeries(updateList);

      setEditStatus('Saved :D');
      setTimeout(() => setEditingSerie(null), 1000);
    } catch (err) {
      console.error(err);
      setEditStatus(`Oepsie woepsie kleine fucky wucky: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${editingSerie.title}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/series?id=eq.${editingSerie.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setOriginalSeries((prev) => prev.filter((s) => s.id !== editingSerie.id));
      setSeries((prev) => prev.filter((s) => s.id !== editingSerie.id));
      setEditingSerie(null);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  const fetchSeries = useCallback(async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/series?order=created_at.desc&select=*&limit=1000`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to fetch series');
      const allSeries = await res.json();
      setOriginalSeries(allSeries);
      setSeries(allSeries);
    } catch (err) {
      console.error('Failed to fetch series:', err);
    }
  }, []);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  const applyFiltersAndSort = (opts = {}) => {
    const currentSearch = opts.searchTerm !== undefined ? opts.searchTerm : searchTerm;
    const currentRating = opts.ratingFilter !== undefined ? opts.ratingFilter : ratingFilter;
    const currentDate = opts.dateFilter !== undefined ? opts.dateFilter : dateFilter;
    const currentUniverse = opts.universeFilter !== undefined ? opts.universeFilter : universeFilter;
    const currentWatchedWith = opts.watchedWithFilter !== undefined ? opts.watchedWithFilter : watchedWithFilter;
    const currentSort = opts.sortOrder !== undefined ? opts.sortOrder : sortOrder;

    let filtered = [...originalSeries];

    if (currentRating) {
      const min = parseFloat(currentRating);
      const max = min + 0.9;
      filtered = filtered.filter(
        (serie) => serie.rating >= min && serie.rating <= max
      );
    }

    if (currentSearch) {
      filtered = filtered.filter((serie) =>
        serie.title.toLowerCase().includes(currentSearch.toLowerCase())
      );
    }

    if (currentDate) {
      const now = new Date();
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - parseInt(currentDate));
      filtered = filtered.filter(
        (serie) => new Date(serie.created_at) >= pastDate
      );
    }

    if (currentUniverse === 'marvel') {
      filtered = filtered.filter((serie) => serie.marvel === true);
    } else if (currentUniverse === 'dc') {
      filtered = filtered.filter((serie) => serie.dc === true);
    }

    if (currentWatchedWith === 'Joy') {
      filtered = filtered.filter((serie) => serie.watched_with_joy === true);
    } else if (currentWatchedWith === 'Rick') {
      filtered = filtered.filter((serie) => serie.watched_with_rick === true);
    } else if (currentWatchedWith === 'Lisa') {
      filtered = filtered.filter((serie) => serie.watched_with_lisa === true);
    } else if (currentWatchedWith === 'Gionnan') {
      filtered = filtered.filter((serie) => serie.watched_with_gionnan === true);
    }

    filtered.sort(byTagAtOrCreatedAt(currentWatchedWith, WATCHED_AT_FIELD));

    if (currentSort === 'highest') {
      filtered.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
    } else if (currentSort === 'lowest') {
      filtered.sort((a, b) => parseFloat(a.rating) - parseFloat(b.rating));
    }

    setSeries(filtered);
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    applyFiltersAndSort({ searchTerm: val });
  };

  const onRatingChange = (e) => {
    const val = e.target.value;
    setRatingFilter(val);
    applyFiltersAndSort({ ratingFilter: val });
  };

  const onDateChange = (e) => {
    const val = e.target.value;
    setDateFilter(val);
    applyFiltersAndSort({ dateFilter: val });
  };

  const onUniverseChange = (e) => {
    const val = e.target.value;
    setUniverseFilter(val);
    applyFiltersAndSort({ universeFilter: val });
  };

  const onWatchedWithChange = (e) => {
    const val = e.target.value;
    setWatchedWithFilter(val);
    applyFiltersAndSort({ watchedWithFilter: val });
  };

  const onSortChange = (order) => {
    setSortOrder(order);
    applyFiltersAndSort({ sortOrder: order });
  };

  const personOptions = availablePeople(originalSeries, (value) => WATCHED_WITH_FIELD[value]);

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Serie Reviews</h1>
          <button onClick={() => navigate('/todo-series')} style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', background: 'white', border: '2px solid black', borderRadius: '8px', padding: '0.2em 0.6em', cursor: 'pointer' }}>To-Do</button>
        </div>
        <div className="controls">
          {user && <button onClick={() => setShowAddForm(true)}>Add +</button>}
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={onSearchChange}
          />
          <button onClick={() => onSortChange('highest')} className={sortOrder === 'highest' ? 'btn-active' : ''}>Highest Rating</button>
          <button onClick={() => onSortChange('lowest')} className={sortOrder === 'lowest' ? 'btn-active' : ''}>Lowest Rating</button>
          <button onClick={() => onSortChange('original')} className={sortOrder === 'original' ? 'btn-active' : ''}>Newest</button>

          <select value={ratingFilter} onChange={onRatingChange}>
            <option value="">Filter by Rating</option>
            {[...Array(10)].map((_, i) => (
              <option key={i} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>

          <select value={dateFilter} onChange={onDateChange}>
            <option value="">Filter by Date</option>
            <option value="7">Last Week</option>
            <option value="30">Last Month</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>

          <select value={universeFilter} onChange={onUniverseChange}>
            <option value="">Marvel/DC</option>
            <option value="marvel">Marvel</option>
            <option value="dc">DC</option>
          </select>

          <select value={watchedWithFilter} onChange={onWatchedWithChange}>
            <option value="">Filter by person</option>
            {personOptions.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="movies-container">
        {series.map((serie, idx) => (
          <div
            className="movie-card"
            key={idx}
            ref={(el) => (cardRefs.current[idx] = el)}
          >
            {serie.poster_url && (
              <div className="movie-poster-wrap">
                <img
                  src={serie.poster_url}
                  alt={`${serie.title} Poster`}
                  className="movie-poster"
                  loading="lazy"
                  onDoubleClick={user ? () => downloadCardImage(cardRefs.current[idx], serie.title) : undefined}
                  onTouchStart={user ? () => downloadLongPress.start(() => downloadCardImage(cardRefs.current[idx], serie.title)) : undefined}
                  onTouchEnd={downloadLongPress.cancel}
                  onTouchCancel={downloadLongPress.cancel}
                  crossOrigin="anonymous"
                />
              </div>
            )}

            <div className="movie-content">
              <div className="movie-header">
                <h2>{serie.title}</h2>
                <h3>
                  <span className="series-season-label-full">Season: </span>
                  <span className="series-season-label-short">S</span>
                  {serie.season}
                </h3>
                <span
                  className="movie-rating"
                  onDoubleClick={user ? () => openEdit(serie) : undefined}
                  onTouchStart={user ? () => editLongPress.start(() => openEdit(serie)) : undefined}
                  onTouchEnd={editLongPress.cancel}
                  onTouchCancel={editLongPress.cancel}
                >{Number(serie.rating).toFixed(1)}</span>
              </div>
              <div className="movie-thoughts">
                {serie.thoughts || '*'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {series.length === 0 && (
        <p className="no-results">Geen ene tering hier man.</p>
      )}

      {showAddForm && (
        <Modal onClose={() => setShowAddForm(false)}>
          <SeriesForm onClose={() => { setShowAddForm(false); fetchSeries(); }} />
        </Modal>
      )}

      {editingSerie && (
        <Modal onClose={() => setEditingSerie(null)}>
          <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontFamily: 'angelWish', fontSize: '2.5rem', letterSpacing: '0.06em', margin: '0 0 1rem 0' }}>Edit Series</h2>
            <div className="form-group"><label>Title *</label><input type="text" name="title" value={editForm.title} onChange={handleEditChange} required /></div>
            <div className="form-group"><label>Season</label><input type="text" name="season" value={editForm.season} onChange={handleEditChange} /></div>
            <div className="form-group"><label>Rating *</label><input type="number" name="rating" value={editForm.rating} onChange={handleEditChange} min="1" max="10" step="0.1" required /></div>
            <div className="form-group"><label>Thoughts</label><textarea name="thoughts" value={editForm.thoughts} onChange={handleEditChange} rows={4} /></div>
            <div className="form-group">
              <label>Poster</label>
              {editingSerie?.poster_url && !editPoster && (
                <img src={editingSerie.poster_url} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={(e) => setEditPoster(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label>Marvel/DC</label>
              <div className="form-checkboxes">
                <label><input type="checkbox" name="marvel" checked={editForm.marvel} onChange={handleEditChange} /> Marvel</label>
                <label><input type="checkbox" name="dc" checked={editForm.dc} onChange={handleEditChange} /> DC</label>
              </div>
            </div>
            <div className="form-group">
              <label>With</label>
              <div className="form-checkboxes">
                <label><input type="checkbox" name="watchedWithJoy" checked={editForm.watchedWithJoy} onChange={handleEditChange} /> Joy &lt;3</label>
                <label><input type="checkbox" name="watchedWithRick" checked={editForm.watchedWithRick} onChange={handleEditChange} /> Rick</label>
                <label><input type="checkbox" name="watchedWithLisa" checked={editForm.watchedWithLisa} onChange={handleEditChange} /> Lisa</label>
                <label><input type="checkbox" name="watchedWithGionnan" checked={editForm.watchedWithGionnan} onChange={handleEditChange} /> Gionnan</label>
              </div>
            </div>
            <div className="edit-modal-actions">
              <button type="button" className="form-submit" style={{ background: '#eee', color: '#333', borderColor: '#999', WebkitTextFillColor: '#333' }} onClick={() => setEditingSerie(null)}>Cancel</button>
              <button type="button" className="form-submit btn-delete" onClick={handleDelete}>Delete</button>
              <button type="submit" className="form-submit" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
            </div>
            {editStatus && <p className="form-status">{editStatus}</p>}
          </form>
        </Modal>
      )}
    </div>
  );
}

export default App;
